<template>
    <div class="quill-editor-container">
        <div id="toolbar" ref="toolbar" class="ql-toolbar">
            <slot name="toolbar">
                <DsDropdown
                    position="bottom-start"
                    disable-scroll
                    class="quill-color-dropdown"
                >
                    <template #default>
                        <div ref="dropdownButton" class="custom-button-div" @click="toggleDropdown">
                            <DsTextButton id="custom-button">
                                <template #leading>
                                    <DsIcon class="icon-with-border" name="circle-fill" :color="selectedColor" />
                                </template>
                                <template #trailing>
                                    <DsIcon name="chevron-down" color="black" />
                                </template>
                            </DsTextButton>
                        </div>
                    </template>
                    <template #menu>
                        <ul class="dropdown-menu-list">
                            <li
                                class="option dropdown-menu-item"
                                @click="$emit('change-note-color', { color: $options.NOTE_COLOR.yellow })"
                            >
                                <DsIcon name="circle-fill" :color="$options.NOTE_COLOR.yellow" />
                                <span class="color-names">{{ $t('funnel.edit.notes.quill.editor.color.yellow') }}</span>
                            </li>
                            <li
                                class="option dropdown-menu-item"
                                @click="$emit('change-note-color', { color: $options.NOTE_COLOR.blue })"
                            >
                                <DsIcon name="circle-fill" :color="$options.NOTE_COLOR.blue" />
                                <span class="color-names">{{ $t('funnel.edit.notes.quill.editor.color.blue') }}</span>
                            </li>
                            <li
                                class="option dropdown-menu-item"
                                @click="$emit('change-note-color', { color: $options.NOTE_COLOR.pink })"
                            >
                                <DsIcon name="circle-fill" :color="$options.NOTE_COLOR.pink" />
                                <span class="color-names">{{ $t('funnel.edit.notes.quill.editor.color.pink') }}</span>
                            </li>
                            <li
                                class="option dropdown-menu-item"
                                @click="$emit('change-note-color', { color: $options.NOTE_COLOR.green })"
                            >
                                <DsIcon name="circle-fill" :color="$options.NOTE_COLOR.green" />
                                <span class="color-names">{{ $t('funnel.edit.notes.quill.editor.color.green') }}</span>
                            </li>
                            <li
                                class="option dropdown-menu-item"
                                @click="$emit('change-note-color', { color: $options.NOTE_COLOR.purple })"
                            >
                                <DsIcon name="circle-fill" :color="$options.NOTE_COLOR.purple" />
                                <span class="color-names">{{ $t('funnel.edit.notes.quill.editor.color.purple') }}</span>
                            </li>
                        </ul>
                    </template>
                </DsDropdown>
                <span class="separator" />
                <span class="ql-formats">
                    <button class="ql-bold" />
                    <button class="ql-italic" />
                    <button class="ql-underline" />
                </span>
                <span class="separator" />
                <span class="ql-formats">
                    <button class="ql-list" value="ordered" />
                    <button class="ql-list" value="bullet" />
                </span>
                <span class="separator" />
            </slot>
        </div>
        <div class="quill-editor">
            <div ref="editorContainer" class="notes-editor-container" />
        </div>
    </div>
</template>

<script>
import Quill from 'quill';
import { NOTE_COLOR } from '../utils/editor.constants';

export default {
    name: 'QuillEditor',
    NOTE_COLOR,

    props: {
        selectedColor: {
            type: String,
            default: NOTE_COLOR.blue,
        },
        noteContent: {
            type: Object,
            default: () => {},
        },
    },
    emits: ['change-note-color'],
    data() {
        return {
            quill: null,
            isDropdownOpen: false,
        };
    },

    mounted() {
        this.initializeEditor();
        document.addEventListener('click', this.handleClickOutside);
    },

    methods: {
        encodeHTMLTags(text) {
            return text.replace(/<(?!\/?(ol|ul|li|strong|em|u|p|br)\b)[^>]+>/g, (match) => {
                return this.encodeHTML(match);
            });
        },

        encodeHTML(text) {
            return text.replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#039;');
        },

        initializeEditor() {
            this.quill = new Quill(this.$refs.editorContainer, {
                modules: {
                    toolbar: this.$refs.toolbar,
                },
                placeholder: this.$t('funnel.edit.notes.quill.editor.placeholder'),
                theme: 'snow',
            });
            this.quill.root.innerHTML = this.encodeHTMLTags(this.noteContent?.cell.value.notes);
        },

        /* For resizing purposes, the container must have overflow: hidden; however, this causes the
            dropdown to be cut off.
            To ensure the dropdown is fully visible when displayed, the overflow property is set to visible
            while the dropdown is open.
         */
        toggleDropdown() {
            this.isDropdownOpen = !this.isDropdownOpen;
            const editorContainer = document.querySelector('.quill-editor-for-notes');

            if (editorContainer) {
                if (this.isDropdownOpen) {
                    editorContainer.style.overflow = 'visible';
                } else {
                    editorContainer.style.overflow = 'hidden';
                }
            }
        },

        handleClickOutside(event) {
            const dropdown = this.$refs.dropdownButton;

            if (dropdown && !dropdown.contains(event.target)) {
                this.isDropdownOpen = false;
                const editorContainer = document.querySelector('.quill-editor-for-notes');

                if (editorContainer) {
                    editorContainer.style.overflow = 'hidden';
                }
            }
        },
    },
};
</script>

<style lang="scss">
@import '~quill/dist/quill.core.css';
@import '~quill/dist/quill.snow.css';
.ql-container.ql-snow {
    border: none;
}
.ql-container {
    overflow: visible;
}
.ql-tooltip.ql-editing {
    border-radius: $border-radius;
}
.quill-color-dropdown .dropdown-menu {
    position: absolute;
    z-index: 9999;
    min-width: px-to-rem(156px);
    left: 0;
    top: 100%
}
.ql-toolbar.ql-snow {
    background: white;
    border: none;
    border-bottom: 1px solid lightgrey;
    padding-top: 0;
    padding-bottom: 0;
    padding-left: 0;
    border-top-left-radius: $border-radius;
    border-top-right-radius: $border-radius;
    overflow: visible;
}
</style>

<style lang="scss" scoped>
$toolbar-height: px-to-rem(60px);
    .quill-editor-container {
        position: relative;
    }
    .notes-editor-container {
        top: px-to-rem(44px);
        max-height: calc(100% - $toolbar-height);
    }

    .quill-editor-container.editable-text-area-overlay {
        border-radius: 8px;
    }

    button#custom-button {
        display: flex;
        justify-content: space-between;
        width: px-to-rem(64px);
        padding-left: px-to-rem(16px);
    }
    #toolbar {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        background: white;
        z-index: 100;
        border-bottom: 1px solid #ccc;
        display: flex;
        align-items: center;
    }

    .custom-button-div {
        display: flex;
        width: px-to-rem(70px);
        justify-content: space-between;
        align-items: center;
        height: px-to-rem(43px);
    }

    .separator {
        width: px-to-rem(1px);
        height: px-to-rem(40px);
        background-color: #ccc;
        margin-right: px-to-rem(8px);
    }
    .color-names {
        padding-left: px-to-rem(16px);
        font-size: $font-size-md;
        font-weight: $font-weight-regular;
        line-height: $line-height-lg;
    }
</style>
